import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

interface Props {
  open: boolean
  onClose: () => void
  side?: 'left' | 'right'
  /** Extra classes for the sliding panel (typically theme-aware bg/border). */
  panelClassName?: string
  children: React.ReactNode
}

/**
 * Slide-in panel + dimmed overlay for mobile navigation. Hidden at `lg`
 * breakpoint and up — desktop layouts keep their permanent sidebar/nav.
 *
 * Closes on overlay click and Esc. Locks body scroll while open so the
 * background doesn't jitter behind the panel.
 */
export function MobileDrawer({
  open,
  onClose,
  side = 'right',
  panelClassName,
  children,
}: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[60] lg:hidden',
        open ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      aria-hidden={!open}
    >
      <div
        className={cn(
          'absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'absolute top-0 bottom-0 w-[85vw] max-w-sm shadow-2xl overflow-y-auto',
          'transition-transform duration-300 ease-out',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          open
            ? 'translate-x-0'
            : side === 'right'
              ? 'translate-x-full'
              : '-translate-x-full',
          panelClassName,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
